import network from '../platform/network';
import useNetworkStatus from '../hooks/useNetworkStatus';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button } from '@mui/material';
import { useDispatch, useStore } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { biometrics } from '../platform/biometrics';
import { runtime } from '../platform/runtime';
import { initializeAuth } from '../middleware/authMiddleware';
import { setAuthChecking } from '../store/authSlice';

export default function BiometricLogin() {
    const connected = useNetworkStatus();
    const [status, setStatus] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const pending = useRef(false);
    const mounted = useRef(true);
    useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const store = useStore();
    const login = useCallback(async () => {
        if (pending.current || store.getState().auth.isAuthenticated) return;
        pending.current = true;
        setBusy(true); setError('');
        try {
            if (!(await network.getStatus()).connected) {
                setError('No internet connection. Reconnect to log in.');
                return;
            }
            await biometrics.unlock();
            if (!mounted.current) return;
            // Keep the login screen mounted so cancellation/retry state survives.
            const outcome = await initializeAuth(store, { showLoading: false });
            if (!mounted.current) return;
            const auth = store.getState().auth;
            if (auth.isAuthenticated) {
                biometrics.didLogin();
                navigate(auth.signedEula ? '/dashboard' : '/eula');
            } else if (outcome === 'unavailable') {
                setError('Unable to connect. Check your internet connection and try again.');
            } else setError('Could not log in. You can use biometrics again or enter your password.');
        } catch (error) {
            dispatch(setAuthChecking(false));
            setError(error.code === 'SESSION_REFRESH_UNAVAILABLE'
                ? 'Unable to connect. Check your internet connection and try again.'
                : error.response?.status === 401
                    ? 'Saved sign-in has expired or was revoked. Please enter your password.'
                    : 'Biometric login was cancelled or unsuccessful. Try again or use your password.');
        } finally { pending.current = false; setBusy(false); }
    }, [dispatch, navigate, store]);
    useEffect(() => {
        if (!runtime.isNative()) return;
        let active = true, statusRequest = 0;
        const check = async () => {
            const request = ++statusRequest;
            try {
                const current = await biometrics.status();
                if (!active || request !== statusRequest) return;
                setStatus(current);
                if (connected === true && current.enabled && !store.getState().auth.isAuthenticated && biometrics.claimAutomaticLogin()) void login();
            } catch { /* Password login remains available. */ }
        };
        void check();
        const unsubscribe = biometrics.onAutomaticLogin(check);
        return () => { active = false; unsubscribe(); };
    }, [login, store, connected]);
    if (!runtime.isNative() || !status?.enabled) return null;
    return <>
        <Button fullWidth variant="outlined" disabled={busy || connected !== true} onClick={login} sx={{ mb: 2 }}>
            {`Log in with ${status.label}`}
        </Button>
        {error && <Alert severity="info">{error}</Alert>}
    </>;
}
